import {
  ECardinality,
  IAction,
  IApiManagement,
  IApiManagementDto,
  IApiMethods,
  IAuthorizationDto,
  IMethodInput,
  IRoleDto
} from "@kuika/kuika-cl-model";
import { EApiInputStrategy } from "@kuika/kuika-cl-model/src/enums/api-input-strategy";
import { Guid } from "guid-typescript";
import _ from "lodash";
import {
  addMethodInput,
  effectiveMethodInputs,
  replaceMethodInput
} from "../../../components/design-tools/api-management/api-builder-model";
import { ApiManagementService } from "../../../services/api-management-service";
import { notifyDesignerLiveToolIdle } from "../../../services/designer-live/designer-live";
import { DesignerPresenceReporter } from "../../../services/designer-live/designer-presence";
import { DesignerSyncService } from "../../../services/designer-live/designer-sync-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface IDynamicInputEntry {
  isDynamic: boolean;
  jsonValue: string;
}

export interface IDynamicInputMap {
  [methodId: string]: IDynamicInputEntry;
}

export interface ApiManagementProviderState {
  selectedApiId: string | undefined;
  apiManagements: IApiManagement[];
  loadApiManagements: (projectId: string) => void;
  saveApiManagements: (api: IApiManagementDto) => Promise<void>;
  getSelectedApi: () => IApiManagement | undefined;
  updateApiManagement: (apiDto: IApiManagementDto) => void;
  setActiveApi: (apiId: string) => void;
  addActionToMethod: (action: IAction) => void;
  deleteActionOfApiMethod: (action: IAction) => void;
  cancelEditingApi: () => void;
  deleteApi: () => void;
  editingApiMethodId: string | undefined;
  setEditingMethod: (methodId: string | undefined) => void;
  cancelEditingMethod: () => void;
  setActiveMethod: (methodId: string | undefined) => void;
  cancelActiveMethod: () => void;
  activeApiMethodId: string | undefined;
  getSelectedApiMethod: () => IApiMethods | undefined;
  createMethodInput: (methodInput: IMethodInput) => void;
  updateMethodInput: (methodInput: IMethodInput) => void;
  deleteMethodInput: (methodInput: IMethodInput) => void;
  updateApiAuthorization: (role: IRoleDto) => void;
  allowApiAnonymousAccessChanged: () => void;
  allowApiAllRolesChanged: () => void;
  getDynamicInputForMethod: (methodId: string) => IDynamicInputEntry | undefined;
  saveDynamicInputForMethod: (methodId: string, entry: IDynamicInputEntry) => void;
  saveApiMethod: (method: IApiMethods) => void;
}

export class ApiManagementProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  ApiManagementProviderState
> {
  static defaultState: ApiManagementProviderState = {
    selectedApiId: undefined,
    apiManagements: [],
    loadApiManagements: () => {},
    saveApiManagements: () => new Promise(() => {}),
    getSelectedApi: () => undefined,
    updateApiManagement: () => {},
    setActiveApi: () => {},
    addActionToMethod: () => {},
    deleteActionOfApiMethod: () => {},
    cancelEditingApi: () => {},
    deleteApi: () => {},
    editingApiMethodId: undefined,
    setEditingMethod: () => {},
    cancelEditingMethod: () => {},
    setActiveMethod: () => {},
    cancelActiveMethod: () => {},
    activeApiMethodId: undefined,
    getSelectedApiMethod: () => undefined,
    createMethodInput: () => {},
    updateMethodInput: () => {},
    deleteMethodInput: () => {},
    updateApiAuthorization: () => {},
    allowApiAnonymousAccessChanged: () => {},
    allowApiAllRolesChanged: () => {},
    getDynamicInputForMethod: () => undefined,
    saveDynamicInputForMethod: () => {},
    saveApiMethod: () => {}
  };

  getProviderValues = (): ApiManagementProviderState => {
    return {
      selectedApiId: this.state.selectedApiId,
      apiManagements: this.state.apiManagements,
      loadApiManagements: this.loadApiManagements,
      saveApiManagements: this.saveApiManagements,
      getSelectedApi: this.getSelectedApi,
      updateApiManagement: this.updateApiManagement,
      setActiveApi: this.setActiveApiManagement,
      addActionToMethod: this.addActionToMethod,
      deleteActionOfApiMethod: this.deleteActionOfApiMethod,
      cancelEditingApi: this.cancelEditingApi,
      deleteApi: this.deleteApi,
      editingApiMethodId: this.state.editingApiMethodId,
      setEditingMethod: this.setEditingMethod,
      cancelEditingMethod: this.cancelEditingMethod,
      setActiveMethod: this.setActiveMethod,
      cancelActiveMethod: this.cancelActiveMethod,
      activeApiMethodId: this.state.activeApiMethodId,
      getSelectedApiMethod: this.getSelectedApiMethod,
      createMethodInput: this.createMethodInput,
      updateMethodInput: this.updateMethodInput,
      deleteMethodInput: this.deleteMethodInput,
      updateApiAuthorization: this.updateApiAuthorization,
      allowApiAnonymousAccessChanged: this.allowApiAnonymousAccessChanged,
      allowApiAllRolesChanged: this.allowApiAllRolesChanged,
      getDynamicInputForMethod: this.getDynamicInputForMethod,
      saveDynamicInputForMethod: this.saveDynamicInputForMethod,
      saveApiMethod: this.saveApiMethod
    };
  };

  updateApiManagement = (apiDto: IApiManagementDto) => {
    const { apiManagements } = this.state;
    const apiManagement = apiManagements.find((api) => api.id === apiDto.id);

    const apiManagementIndex = apiManagements.findIndex((api) => api.id === apiDto.id);
    if (apiManagementIndex !== undefined && apiManagementIndex > -1 && apiManagement) {
      apiManagements[apiManagementIndex] = apiManagement;
      this.setState({ apiManagements });
    }
  };

  getSelectedApi = () => {
    const { selectedApiId } = this.state;
    const { apiManagements } = this.state;
    const apiManagement = apiManagements.find((api) => api.id === selectedApiId);

    if (typeof apiManagement?.json === "string") {
      apiManagement.json = JSON.parse(apiManagement.json);
    }

    return apiManagement;
  };

  loadApiManagements = async (projectId) => {
    try {
      if (projectId !== undefined) {
        const { project } = this.state;
        const res = await ApiManagementService.GetAllByProjectId(projectId);

        if (res && res.status === 200) {
          const apiManagements = res.data;
          apiManagements.forEach((api) => {
            if (api.json) {
              api.json = JSON.parse(api.json);
            }
          });
          this.setState({ apiManagements });
          project.apiManagements = apiManagements;
        }
        this.partial.projectProvider.updateProject(project);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  saveApiManagements = async (api: IApiManagementDto): Promise<void> => {
    const proceed: boolean = await DesignerSyncService.checkStaleWrite("api.changed", api?.id, api?.name ?? "API");
    if (!proceed) return;
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        if (api === undefined) {
          throw new Error("An error occurred while saving api.");
        }

        const apiManagement = this.state.apiManagements?.find((x) => x.id === api.id);
        const cloneApi: IApiManagementDto = _.cloneDeep(api);

        if (cloneApi.json && typeof cloneApi.json !== "string") {
          cloneApi.json = JSON.stringify(cloneApi.json);
        }

        if (apiManagement) {
          await ApiManagementService.Update(cloneApi.id, cloneApi);
        } else {
          await ApiManagementService.Create(cloneApi);
        }

        this.loadApiManagements(this.state.project.id);
        resolve();
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
        reject(error);
      }
    });
  };

  deleteApi = async () => {
    const apis = this.state.apiManagements;
    let api: any;
    api = this.getSelectedApi();
    if (api) {
      const deletedApiId = api.id;
      const res = await ApiManagementService.Delete(api.id);
      if (res) {
        this.saveApiManagements(api)
          .then(() => {
            if (api) apis?.splice(apis.indexOf(api), 1);
            this.setState({ apiManagements: apis, selectedApiId: undefined });
            KMainFunctions.displaySuccessNotification("Api has been deleted!");
            this.setActiveApiManagement(undefined);
            this.partial.startProcessTriggerSync.pruneApiManagement(deletedApiId);
            this.loadApiManagements(this.state.project.id);
            this.cancelEditingApi();
            this.cancelActiveMethod();
          })
          .catch(() => {});
      } else {
        KMainFunctions.displayErrorNotification("An error occured while deleting Api!");
      }
    }
  };

  addActionToMethod = (action: IAction) => {
    const api = this.getSelectedApi();
    if (api && action) {
      const method = api.json?.methods.find((method) => method.id === this.state.activeApiMethodId);
      if (!method.actions || method.actions?.length === 0) method.actions = [];
      const currentActionIndex = method?.actions.findIndex((a) => a.id == action.id);
      // Capture the replaced action before the swap so the StartProcess diff sees its
      // previous target workflow (UI create/edit funnels through here).
      const oldAction = currentActionIndex > -1 ? method.actions[currentActionIndex] : undefined;
      if (currentActionIndex > -1) {
        method.actions[currentActionIndex] = action;
      } else {
        method.actions?.push(action);
      }
      // Central StartProcess trigger sync for API create/edit via the UI. snapshot()
      // resolves StartProcess actions authoritatively via actionDefinitionId and
      // returns [] for anything else, so unrelated saves no-op out below.
      const triggerSync = this.partial.startProcessTriggerSync;
      const previousSnapshot = triggerSync.snapshot(oldAction ? [oldAction] : []);
      const currentSnapshot = triggerSync.snapshot([action]);
      if (previousSnapshot.length > 0 || currentSnapshot.length > 0) {
        triggerSync.applyDiff({
          sourceType: "ApiManagement",
          sourceId: api.id,
          previousSnapshot,
          currentSnapshot
        });
      }
      this.saveApiManagements(api).catch(() => {});
    }
  };

  deleteActionOfApiMethod = (action: IAction) => {
    const api = this.getSelectedApi();
    if (api && action) {
      const method = api.json?.methods.find((method) => method.id === this.state.activeApiMethodId);
      const triggerSync = this.partial.startProcessTriggerSync;
      if (!method.actions) method.actions = [];
      const currentActionIndex = method?.actions.findIndex((a) => a.id == action.id);
      if (currentActionIndex > -1) {
        method.actions.splice(currentActionIndex, 1);
      }
      // Snapshot the deleted action's subtree; isStartProcessAction resolves it
      // authoritatively via actionDefinitionId, so a non-StartProcess action yields an
      // empty snapshot and the index update is skipped.
      const removedSnapshot = triggerSync.snapshot([action]);
      if (removedSnapshot.length > 0) {
        triggerSync.applyDiff({
          sourceType: "ApiManagement",
          sourceId: api.id,
          previousSnapshot: removedSnapshot,
          currentSnapshot: []
        });
      }
      this.saveApiManagements(api).catch(() => {});
    }
  };

  createMethodInput = (methodInput: IMethodInput) => {
    const api = this.getSelectedApi();
    if (api && methodInput) {
      const method = api.json?.methods?.find((method) => method.id === this.state.activeApiMethodId);
      const exists = method?.inputMethods?.find((inputMethod) => inputMethod.name === methodInput.name);
      if (exists) {
        KMainFunctions.displayWarningNotification("Method input with this name is already exists.");
      } else {
        // Into the List of Objects wrapper, creating it on the first input; a List method whose
        // inputs were left at the top level by the old "+" gets them gathered into it here.
        addMethodInput(method, methodInput);
        this.saveApiManagements(api).catch(() => {});
      }
    }
  };

  updateMethodInput = (methodInput: IMethodInput) => {
    const api = this.getSelectedApi();

    if (api && methodInput) {
      const method = api.json.methods?.find((method) => method.id === this.state.activeApiMethodId);
      const exists = method?.inputMethods?.find(
        (inputMethod) => inputMethod.name === methodInput.name && inputMethod.id !== methodInput.id
      );

      if (exists) {
        KMainFunctions.displayWarningNotification("Method input with this name is already exists.");
        return;
      }

      if (!method.inputMethods) method.inputMethods = [];
      replaceMethodInput(method, methodInput);

      this.saveApiManagements(api).catch(() => {});
    }
  };

  deleteMethodInput = (methodInput: IMethodInput) => {
    const api = this.getSelectedApi();
    if (api && methodInput) {
      const method = api.json?.methods?.find((method) => method.id === this.state.activeApiMethodId);
      if (!method.inputMethods) method.inputMethods = [];
      // Through the List of Objects wrapper, like the update's replacement.
      const inputs: IMethodInput[] = effectiveMethodInputs(method);
      const currentMethodIndex: number = inputs.findIndex((a: IMethodInput): boolean => a.id === methodInput.id);
      if (currentMethodIndex > -1) inputs.splice(currentMethodIndex, 1);
      this.saveApiManagements(api).catch(() => {});
    }
  };

  getSelectedApiMethod = () => {
    const api = this.getSelectedApi();
    const method: IApiMethods = api?.json?.methods?.find((method) => method.id === this.state.activeApiMethodId);
    return method;
  };

  saveApiMethod = async (method: IApiMethods) => {
    const api = this.getSelectedApi();
    if (api && method) {
      const proceed: boolean = await DesignerSyncService.checkStaleWrite("api.changed", api.id, api.name ?? "API");
      if (!proceed) return;
      const methodIndex = api.json?.methods?.findIndex((m) => m.id === method.id);
      if (methodIndex !== undefined && methodIndex > -1) {
        // Central StartProcess trigger sync for API via MCP (the agent funnels method
        // saves/deletes through here). Whole-method diff: snapshot the persisted method
        // before overwriting it, then compare against the incoming one — covers
        // create / edit / delete of actions in a single point.
        const previousMethod = api.json.methods[methodIndex];
        const triggerSync = this.partial.startProcessTriggerSync;
        const previousSnapshot = triggerSync.snapshot(previousMethod?.actions);
        const currentSnapshot = triggerSync.snapshot(method?.actions);
        api.json.methods[methodIndex] = method;
        if (previousSnapshot.length > 0 || currentSnapshot.length > 0) {
          triggerSync.applyDiff({
            sourceType: "ApiManagement",
            sourceId: api.id,
            previousSnapshot,
            currentSnapshot
          });
        }
        this.saveApiManagements(api).catch(() => {});
      }
    }
  };

  setActiveApiManagement = (apiId: any) => {
    this.setState({ selectedApiId: apiId }, () => {
      if (!apiId) notifyDesignerLiveToolIdle();
    });
    if (apiId) {
      const apiName: string | undefined = this.state.apiManagements?.find((api) => api.id === apiId)?.name;
      DesignerPresenceReporter.reportEntity("api", apiId, apiName, "api.changed");
    } else {
      DesignerPresenceReporter.reportEntity("api", undefined, undefined);
    }
  };

  cancelEditingApi = () => {
    DesignerPresenceReporter.reportEntity("api", undefined, undefined);
    this.setState({ selectedApiId: undefined }, () => notifyDesignerLiveToolIdle());
  };

  setEditingMethod = (methodId: any) => {
    this.setState({ editingApiMethodId: methodId });
  };

  cancelEditingMethod = () => {
    this.setState({ editingApiMethodId: undefined });
  };

  setActiveMethod = (methodId: any) => {
    this.setState({ activeApiMethodId: methodId });
  };

  cancelActiveMethod = () => {
    this.setState({ activeApiMethodId: undefined });
  };

  // The authorization panel reads an unset authorization as "every role"; seeding any other
  // default would make the first toggle flip the seed back to what the panel already showed.
  defaultAuthorizationModel = () => {
    return {
      permittedRoles: [],
      hide: false,
      disable: false,
      allowAnonymousAccess: false,
      allowAllRoles: true
    } as IAuthorizationDto;
  };

  // The authorization handlers mutate the method in place, so re-render now instead of waiting
  // for the save's reload (setState stamps a fresh change reason, which re-renders the panel),
  // and reload when the save fails so the panel drops the value that never reached the server.
  commitApiAuthorization = (api: IApiManagementDto): void => {
    this.setState({ apiManagements: this.state.apiManagements });
    this.saveApiManagements(api).catch((): Promise<void> => this.loadApiManagements(this.state.project.id));
  };

  allowApiAnonymousAccessChanged = () => {
    const api = this.getSelectedApi();
    const apiMethod = this.getSelectedApiMethod();
    if (api && apiMethod) {
      if (!apiMethod?.authorization) {
        apiMethod.authorization = this.defaultAuthorizationModel();
      }
      this.setallowApiAnonymousAccessChanged(apiMethod.authorization);
      this.commitApiAuthorization(api);
    }
  };

  setallowApiAnonymousAccessChanged = (authorization: IAuthorizationDto) => {
    authorization.allowAnonymousAccess = !authorization.allowAnonymousAccess;
    authorization.allowAllRoles = true;
  };

  allowApiAllRolesChanged = () => {
    const api = this.getSelectedApi();
    const apiMethod = this.getSelectedApiMethod();
    if (api && apiMethod) {
      if (!apiMethod.authorization) {
        apiMethod.authorization = this.defaultAuthorizationModel();
      }
      this.setallowApiAllRolesChanged(apiMethod.authorization);
      this.commitApiAuthorization(api);
    }
  };

  setallowApiAllRolesChanged = (authorization: IAuthorizationDto) => {
    authorization.allowAllRoles = !authorization.allowAllRoles;
    authorization.hide = true;
    authorization.disable = false;
  };

  updateApiAuthorization = (role: IRoleDto) => {
    const api = this.getSelectedApi();
    const apiMethod = this.getSelectedApiMethod();
    if (api && apiMethod) {
      if (!apiMethod.authorization) {
        apiMethod.authorization = this.defaultAuthorizationModel();
      }
      this.setUpdateApiAuthorization(role, apiMethod.authorization);
      this.commitApiAuthorization(api);
    }
  };

  setUpdateApiAuthorization = (role: IRoleDto, authorization: IAuthorizationDto) => {
    if (authorization.permittedRoles.includes(role.id)) {
      authorization.permittedRoles.splice(authorization.permittedRoles.indexOf(role.id), 1);
    } else authorization.permittedRoles.push(role.id);
  };

  parseDynamicInputMap = (api: IApiManagement): IDynamicInputMap => {
    if (!api?.dynamicInput) return {};
    try {
      return JSON.parse(api.dynamicInput) as IDynamicInputMap;
    } catch {
      return {};
    }
  };

  getDynamicInputForMethod = (methodId: string): IDynamicInputEntry | undefined => {
    const api = this.getSelectedApi();
    if (!api || !methodId) return undefined;
    const map = this.parseDynamicInputMap(api);
    return map[methodId];
  };

  saveDynamicInputForMethod = (methodId: string, entry: IDynamicInputEntry) => {
    const api = this.getSelectedApi();
    if (!api || !methodId) return;

    const map = this.parseDynamicInputMap(api);
    map[methodId] = entry;
    api.dynamicInput = JSON.stringify(map);

    // When dynamic mode is on and JSON is present, convert JSON to method inputs (with nested support)
    if (entry.isDynamic && entry.jsonValue?.trim()) {
      const method = api.json?.methods?.find((m: IApiMethods) => m.id === methodId);
      if (method) {
        const inputs = this.convertDynamicJsonToMethodInputsDeep(entry.jsonValue);
        if (inputs.length === 1 && inputs[0].isObject && inputs[0].cardinality === ECardinality.Many) {
          method.inputStrategy = EApiInputStrategy.ListOfObjects;
        } else {
          // Keep inputStrategy in sync with the freshly parsed shape; a stale ListOfObjects
          // would make the panel read inputMethods[0].parameters (now undefined) and the picker
          // read the flat inputMethods, hiding the fields.
          method.inputStrategy = EApiInputStrategy.Object;
        }
        method.inputMethods = inputs;
      }
    }

    this.saveApiManagements(api).catch(() => {});
  };

  /**
   * Infers the Kuika type and cardinality from a JSON value based on the following rules:
   *
   * | Value Type       | Kuika Type      | Cardinality |
   * |-----------------|-----------------|-------------|
   * | integer number  | Int64 (long)    | One         |
   * | float number    | Decimal         | One         |
   * | ISO date w/o TZ | DateTime        | One         |
   * | ISO date w/ TZ  | DateTimeOffset  | One         |
   * | Guid string     | Guid            | One         |
   * | other string    | String          | One         |
   * | boolean         | Boolean         | One         |
   * | array           | (element type)  | Many        |
   * | object          | object          | One         |
   * | null/undefined  | String          | One         |
   */
  private inferKuikaType = (value: any, _key: string): { type: string; cardinality: ECardinality } => {
    // null/undefined → String
    if (value === null || value === undefined) {
      return { type: "String", cardinality: ECardinality.One };
    }

    // Boolean
    if (typeof value === "boolean") {
      return { type: "Boolean", cardinality: ECardinality.One };
    }

    // Number: integer → Int64 (long), float → Decimal
    if (typeof value === "number") {
      if (Number.isInteger(value)) {
        return { type: "Int64", cardinality: ECardinality.One };
      }
      return { type: "Decimal", cardinality: ECardinality.One };
    }

    // String: check for Guid, DateTimeOffset, DateTime
    if (typeof value === "string") {
      // Check for Guid format
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (guidRegex.test(value)) {
        return { type: "Guid", cardinality: ECardinality.One };
      }

      // Check for DateTimeOffset (ISO date with Z or timezone offset)
      const dtoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;
      if (dtoRegex.test(value)) {
        return { type: "DateTimeOffset", cardinality: ECardinality.One };
      }

      // Check for DateTime (ISO date without timezone offset)
      const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/;
      if (dateRegex.test(value) && !isNaN(Date.parse(value))) {
        return { type: "DateTime", cardinality: ECardinality.One };
      }

      return { type: "String", cardinality: ECardinality.One };
    }

    // Array → Many cardinality, infer element type from first element
    if (Array.isArray(value)) {
      if (value.length > 0) {
        const elementTypeInfo = this.inferKuikaType(value[0], _key);
        return { type: elementTypeInfo.type, cardinality: ECardinality.Many };
      }
      return { type: "String", cardinality: ECardinality.Many };
    }

    // Object → object
    if (typeof value === "object") {
      return { type: "object", cardinality: ECardinality.One };
    }

    return { type: "String", cardinality: ECardinality.One };
  };

  /**
   * Converts a dynamic JSON string into a nested array of parameter objects
   * matching the ActionDefinitionParameterModel / IActionParameter structure
   * (similar to SAP action parameter flow).
   *
   * Original field names are preserved at every level — the hierarchy itself
   * (via `parameters[]`) captures the nesting.
   *
   * Nested objects/arrays produce parameters with:
   *   isObject: true, objectClassName, parameters: [children], realPropName, displayName
   * Child fields include:
   *   ownerClassName, ownerPropName, realPropName, displayName, isMandatory
   */
  private convertDynamicJsonToMethodInputsDeep = (jsonString: string): IMethodInput[] => {
    if (!jsonString || !jsonString.trim()) return [];

    try {
      const parsed = JSON.parse(jsonString);

      // null or non-object -> nothing useful
      if (parsed === null || typeof parsed !== "object") {
        return [];
      }

      const inputs: IMethodInput[] = [];

      // Top-level array: wrap as a single parameter called "items" using existing builder
      if (Array.isArray(parsed)) {
        const param = this.buildNestedParameterFromJson(parsed, "Items", undefined, undefined);
        inputs.push(param);
        return inputs;
      }

      for (const key of Object.keys(parsed)) {
        const value = parsed[key];
        const param = this.buildNestedParameterFromJson(value, key, undefined, undefined);
        inputs.push(param);
      }
      return inputs;
    } catch {
      return [];
    }
  };

  /**
   * Recursively builds a single parameter object from a JSON value.
   * For objects / arrays-of-objects, the parameter gets isObject:true,
   * objectClassName and a nested parameters[] array with children.
   *
   * Original field names are preserved — no prefixing.
   * The hierarchy is expressed through the `parameters[]` tree.
   *
   * @param value            The JSON value for this property
   * @param fieldName        The original field name in the JSON (e.g. "id", "changes")
   * @param ownerClassName   The parent's objectClassName (undefined for top-level)
   * @param ownerPropName    The parent's field name (undefined for top-level)
   */
  private buildNestedParameterFromJson = (
    value: any,
    fieldName: string,
    ownerClassName: string | undefined,
    ownerPropName: string | undefined
  ): IMethodInput => {
    // --- Array ---
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === "object" && value[0] !== null && !Array.isArray(value[0])) {
        // Array of objects → object with cardinality Many + nested parameters
        const objClassName = fieldName;
        const childParams = this.buildChildParametersFromJson(value[0], fieldName, objClassName);
        const param: IMethodInput = {
          id: Guid.create().toString(),
          name: fieldName,
          Typename: fieldName,
          type: fieldName,
          cardinality: ECardinality.Many,
          isObject: true,
          objectClassName: objClassName,
          realPropName: fieldName,
          displayName: fieldName,
          parameters: childParams
        };
        if (ownerClassName) {
          param.isMandatory = true;
          param.ownerClassName = ownerClassName;
          param.ownerPropName = ownerPropName;
        } else {
          param.isOptionalParameter = true;
        }
        return param;
      } else if (value.length > 0) {
        // Array of primitives → primitive type with cardinality Many
        const { type } = this.inferKuikaType(value[0], fieldName);
        const param: IMethodInput = {
          id: Guid.create().toString(),
          name: fieldName,
          Typename: type,
          type,
          cardinality: ECardinality.Many,
          realPropName: fieldName,
          displayName: fieldName
        };
        if (ownerClassName) {
          param.isMandatory = true;
          param.ownerClassName = ownerClassName;
          param.ownerPropName = ownerPropName;
        }
        return param;
      } else {
        // Empty array → String with cardinality Many
        const param: IMethodInput = {
          id: Guid.create().toString(),
          name: fieldName,
          Typename: "String",
          type: "String",
          cardinality: ECardinality.Many,
          realPropName: fieldName,
          displayName: fieldName
        };
        if (ownerClassName) {
          param.isMandatory = true;
          param.ownerClassName = ownerClassName;
          param.ownerPropName = ownerPropName;
        }
        return param;
      }
    }

    // --- Object (non-array, non-null) ---
    if (typeof value === "object" && value !== null) {
      const objClassName = fieldName;
      const childParams = this.buildChildParametersFromJson(value, fieldName, objClassName);
      const param: IMethodInput = {
        id: Guid.create().toString(),
        name: fieldName,
        Typename: fieldName,
        type: fieldName,
        cardinality: ECardinality.One,
        isObject: true,
        objectClassName: objClassName,
        realPropName: fieldName,
        displayName: fieldName,
        parameters: childParams
      };
      if (ownerClassName) {
        param.isMandatory = true;
        param.ownerClassName = ownerClassName;
        param.ownerPropName = ownerPropName;
      } else {
        param.isOptionalParameter = true;
      }
      return param;
    }

    // --- Primitive / null / undefined ---
    const { type, cardinality } = this.inferKuikaType(value, fieldName);
    const param: IMethodInput = {
      id: Guid.create().toString(),
      name: fieldName,
      Typename: type,
      type,
      cardinality,
      realPropName: fieldName,
      displayName: fieldName
    };
    if (ownerClassName) {
      param.isMandatory = true;
      param.ownerClassName = ownerClassName;
      param.ownerPropName = ownerPropName;
    }
    return param;
  };

  /**
   * Builds an array of child parameters for a given JSON object.
   * Each key in the object becomes a nested parameter with the parent's
   * objectClassName set as ownerClassName.
   *
   * @param obj                   The JSON object whose keys become children
   * @param parentFieldName       The parent's original field name (used as ownerPropName)
   * @param parentObjectClassName The parent's objectClassName (used as ownerClassName for children)
   */
  private buildChildParametersFromJson = (
    obj: Record<string, any>,
    parentFieldName: string,
    parentObjectClassName: string
  ): IMethodInput[] => {
    const children: IMethodInput[] = [];
    for (const key of Object.keys(obj)) {
      const child = this.buildNestedParameterFromJson(obj[key], key, parentObjectClassName, parentFieldName);
      children.push(child);
    }
    return children;
  };
}
